import React, { useEffect, useRef } from 'react';
import { useAuth } from 'wasp/client/auth';

function pushApiHeaders(jsonBody = false): HeadersInit {
  const headers: Record<string, string> = {};
  if (jsonBody) {
    headers['Content-Type'] = 'application/json';
  }
  try {
    const raw = localStorage.getItem('wasp:sessionId');
    if (raw) {
      const sessionId = JSON.parse(raw) as string;
      if (sessionId) {
        headers.Authorization = `Bearer ${sessionId}`;
      }
    }
  } catch {
    // Session not available yet.
  }
  return headers;
}

async function readJsonResponse<T>(res: Response): Promise<T | null> {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Registers service worker + push subscription for authenticated users.
 * This replaces the legacy polling-based browser Notification API fallback.
 */
export default function NotificationPushManager() {
  const { data: user } = useAuth();
  const subscribedRef = useRef(false);

  useEffect(() => {
    const setupPush = async () => {
      if (!user) return;
      if (subscribedRef.current) return;
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        if (Notification.permission === 'default') {
          await Notification.requestPermission();
        }
        if (Notification.permission !== 'granted') return;

        const keyRes = await fetch('/api/push/public-key', { credentials: 'include' });
        if (!keyRes.ok) return;
        const keyBody = await readJsonResponse<{ publicKey?: string }>(keyRes);
        const publicKey = keyBody?.publicKey;
        if (!publicKey) return;

        let sub = await registration.pushManager.getSubscription();
        if (!sub) {
          sub = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });
        }

        const subscribeRes = await fetch('/api/push/subscribe', {
          method: 'POST',
          credentials: 'include',
          headers: pushApiHeaders(true),
          body: JSON.stringify({
            subscription: sub.toJSON(),
            userAgent: navigator.userAgent,
          }),
        });
        if (!subscribeRes.ok) return;

        subscribedRef.current = true;
      } catch (error) {
        console.error('Push setup failed:', error);
      }
    };

    const teardownPush = async () => {
      if (user) return;
      if (!('serviceWorker' in navigator)) return;
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = await registration?.pushManager.getSubscription();
      if (!sub) return;

      try {
        await fetch('/api/push/unsubscribe', {
          method: 'POST',
          credentials: 'include',
          headers: pushApiHeaders(true),
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
      } catch {
        // Best-effort unregister.
      }

      await sub.unsubscribe().catch(() => undefined);
      subscribedRef.current = false;
    };

    void setupPush();
    void teardownPush();
  }, [user]);

  return null;
}



